import CitySEOHead from "@/components/feature/CitySEOHead";
import OremHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import SharedProcessingTimeSection from "@/components/feature/SharedProcessingTimeSection";

const oremProcessingOptions = [
  {
    speed: "24",
    unit: "Hours*",
    label: "SLC Passport Agency",
    tag: "Emergency Only",
    tagColor: "bg-gray-100 text-gray-500",
    features: [
      "Appointment required — no walk-ins accepted",
      "Life-or-death emergencies only",
      "Bereavement or medical crisis",
      "35 miles north — expect I-15 traffic",
      "Expect to wait all day or return next day",
    ],
    highlight: false,
    emergency: true,
  },
  {
    speed: "2–9",
    unit: "Business Days",
    label: "Fast Passport Center",
    tag: "Most Popular",
    tagColor: "bg-[#F6C344] text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Predictable turnaround — no guessing",
      "Ideal for UVU students, LDS missions, University Parkway tech pros",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: true,
  },
  {
    speed: "3–5",
    unit: "Weeks",
    label: "Fast Passport Center",
    tag: "Standard Expedited",
    tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Faster than self-service",
      "Good for planned travel — family vacations, conferences, study abroad",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: false,
    smartSaver: true,
  },
  {
    speed: "1–2",
    unit: "Months",
    label: "Self-Service (Post Office)",
    tag: "Slowest",
    tagColor: "bg-gray-100 text-gray-400",
    features: [
      "No courier access — standard mail only",
      "Orem, Provo & Lindon post offices book 2–4 weeks out",
      "In-person clerk appointment required",
      "No document review — errors cause rejection",
      "No dedicated agent — you are on your own",
      "Updates only by physical mail letter",
      "No phone or email contact for your case",
      "Can't upgrade speed after submission",
      "Not suitable if travel is within 10 weeks",
    ],
    highlight: false,
    muted: true,
  },
];

export const faqs = [
  {
    q: "Where is the nearest passport agency to Orem?",
    a: "The nearest regional passport agency is the Salt Lake City Passport Agency at 324 S State St, Suite 225 — about 35 miles north of Orem. Walk-in appointments are rarely available. Our courier service bypasses the agency line entirely.",
  },
  {
    q: "How fast can I get a passport in Orem, Utah?",
    a: "Through our expedited courier service, passports can be processed as fast as 24 hours for emergencies. Rush service takes 3-5 business days. Standard expedited takes 5-7 days. We understand Utah Valley's unique travel needs.",
  },
  {
    q: "Do I need to visit an office for Orem passport service?",
    a: "No office visit required for our service. You mail us your documents with our prepaid FedEx label. For first-time applicants and minors, you'll need to visit an acceptance facility before we take over.",
  },
  {
    q: "I'm a UVU student with study abroad in 2 weeks — can you help?",
    a: "Absolutely. Two weeks is enough for our rush or expedited service in most cases. We've processed many Utah Valley University student passports. Place your order immediately and we'll guide you through the fastest path.",
  },
  {
    q: "Can I get a passport for my whole family in Orem?",
    a: "Yes. We process family applications together to minimize turnaround time. Each member needs their own application, but we handle them simultaneously. This is common for Orem families planning international vacations together.",
  },
  {
    q: "What post offices accept passport applications in Orem?",
    a: "The Orem Main Post Office at 760 S State St accepts passport applications. Nearby options include the Provo Post Office (95 W 100 S) and the Lindon Post Office (128 N State St). Appointments are recommended.",
  },
  {
    q: "How do I avoid common mistakes in Orem passport applications?",
    a: "The most common mistakes are: waiting too late for post office appointments, wrong photo size, and incomplete parental consent for minors. We review all your documents before submission.",
  },
  {
    q: "Is your service more affordable than going to the SLC agency?",
    a: "For most travelers, yes. Our courier service is typically 30-100% less than FedEx and other resellers. The agency requires a full-day trip and rarely accepts walk-ins. We handle everything by mail.",
  },
];

export default function OremPage() {
  return (
    <>
      <CitySEOHead
        city="Orem"
        state="Utah"
        stateAbbr="UT"
        route="/orem"
        title="Emergency Passport Orem, UT — Expedited & Fast Passport Service | Fast Passport Center"
        description="Emergency and expedited passport services in Orem, UT — as fast as 24 hours. Serving Utah Valley University students, LDS mission families, Provo/Orem suburban families, and University Parkway business travelers. Registered State Dept. courier. A+ BBB rated."
        geoPlacename="Orem, Utah"
        geoPosition="40.2969;-111.6946"
        faqs={faqs}
        servicesSchema={[
          { name: "Emergency Passport Orem UT", description: "Urgent passport processing for Orem Utah travelers with immediate departure needs from Utah Valley." },
          { name: "Expedited Passport Renewal Orem UT", description: "Fast passport renewal for Orem Utah residents with a previously-issued passport." },
          { name: "First-Time Passport Orem UT", description: "New passport application services for Orem Utah and Utah County residents applying for their first passport." },
          { name: "Child Passport Orem UT", description: "Minor passport services for Orem Utah families with children under 16." },
          { name: "Name Change Passport Orem UT", description: "Passport name correction after marriage, divorce, or legal name changes for Orem residents." },
          { name: "Lost Stolen Passport Orem UT", description: "Replacement passport service for Orem residents with lost or stolen travel documents, including DS-64 guidance." },
          { name: "Second Passport Orem UT", description: "Second passport services for Orem Utah frequent travelers and University Parkway tech professionals." },
        ]}
        keywords="emergency passport Orem, expedited passport Orem Utah, UVU passport, passport renewal Utah County, passport service Orem"
        statePageName="Utah"
        statePageRoute="/states/utah"
      />
      <div className="font-sans">
        <OremHeroSection />

        <div className="bg-[#1A365D] text-white sticky top-0 z-40 hidden lg:block">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex items-center gap-8 h-12 overflow-x-auto">
              {[
                { label: "Processing", href: "#processing" },
                { label: "Services", href: "#services" },
                { label: "Local Area", href: "#local-area" },
                { label: "Travel Timing", href: "#travel-timing" },
                { label: "Testimonials", href: "#testimonials" },
                { label: "Facilities", href: "#facilities" },
                { label: "Compare", href: "#compare" },
                { label: "How It Works", href: "#how-it-works" },
                { label: "FAQ", href: "#faq" },
              ].map((item) => (
                <a key={item.href} href={item.href} className="text-sm text-white/80 hover:text-[#F6C344] whitespace-nowrap transition-colors py-3 border-b-2 border-transparent hover:border-[#F6C344]">
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Processing Time */}
        <SharedProcessingTimeSection
          sectionId="processing"
          cityName="Orem"
          headingExtra="UT"
          introText="The SLC Passport Agency is 35 miles north on I-15 and appointments book out weeks in advance — walk-ins are only for verified life-or-death emergencies. Our registered courier service hand-delivers your application daily, so UVU students, LDS mission families, University Parkway tech professionals, and Utah Valley families get their passports on a predictable timeline — no appointment lottery, no waiting room, no drive to Salt Lake."
          options={oremProcessingOptions}
          emergencyDescription="The SLC Passport Agency at 324 S State St, 35 miles north of Orem, serves verified life-or-death emergencies only. You must call NPIC (877-487-2778) and have proof of imminent international travel within 72 hours. Appointments are extremely limited and not guaranteed — and Orem residents are competing with the entire Wasatch Front for a slot."
          disclaimerText="* Emergency processing availability depends on U.S. Department of State volume, appointment slots, and your specific circumstances. Travel must be within 72 hours for agency emergency appointments. Courier processing times are based on business days and are not guaranteed — but our daily submission schedule gives Orem and Utah Valley residents the fastest legally available route."
          afterCardsContent={
            <div className="mt-16">
              <div className="text-center mb-10">
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-4">Where Orem Travelers Are Going</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {[
                  { city: "Sao Paulo, Brazil", flag: "🇧🇷", note: "LDS mission hub" },
                  { city: "Tokyo, Japan", flag: "🇯🇵", note: "Tech partnerships" },
                  { city: "London, UK", flag: "🇬🇧", note: "Study abroad & business" },
                  { city: "Mexico City, Mexico", flag: "🇲🇽", note: "Family vacation" },
                  { city: "Seoul, South Korea", flag: "🇰🇷", note: "Tech & mission" },
                  { city: "Paris, France", flag: "🇫🇷", note: "Study abroad" },
                ].map((dest) => (
                  <div key={dest.city} className="flex items-center gap-3 bg-[#F7F8FA] rounded-lg p-4">
                    <span className="text-2xl">{dest.flag}</span>
                    <div>
                      <p className="font-bold text-[#1A365D] text-sm">{dest.city}</p>
                      <p className="text-gray-500 text-xs">{dest.note}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { stat: "40,000+", label: "UVU students served" },
                  { stat: "24 hrs", label: "Fastest turnaround" },
                  { stat: "20+ yrs", label: "Serving Utah families" },
                  { stat: "4.9★", label: "Customer rating" },
                ].map((s) => (
                  <div key={s.label} className="text-center bg-[#EEF2F8] rounded-lg p-5">
                    <p className="font-bold text-[#1A365D] text-2xl mb-1">{s.stat}</p>
                    <p className="text-gray-600 text-sm">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        <section id="services" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Handle</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Orem &amp; Utah Valley</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: "ri-passport-line", title: "Passport Renewal", desc: "Adult renewals by mail with no post office visit. Fastest route to a new 10-year passport for previously issued adult passports." },
                { icon: "ri-user-add-line", title: "First-Time Passport", desc: "Complete DS-11 guidance and acceptance facility support for first-time applicants throughout Utah County." },
                { icon: "ri-parent-line", title: "Child Passport", desc: "Minor under 16? Both parents must consent. We guide you through the DS-11 minor requirements and acceptance clerk appointment." },
                { icon: "ri-alert-line", title: "Lost or Stolen Passport", desc: "DS-64 filing plus replacement application. We handle the paperwork and the courier leg together." },
                { icon: "ri-file-edit-line", title: "Name Change Passport", desc: "Post-marriage or legal name change? We process DS-5504 corrections for recently issued passports and full renewals for older ones." },
                { icon: "ri-global-line", title: "Second Passport", desc: "Required for frequent travelers to countries with conflicting visa stamp policies. We handle this specialized request regularly." },
              ].map((svc) => (
                <div key={svc.title} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${svc.icon} text-[#1A365D] text-2xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-lg mb-2">{svc.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="local-area" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Orem Context</p>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-6">Why Orem Residents Need Expedited Passport Help</h2>
                <div className="text-gray-600 text-sm leading-relaxed space-y-4">
                  <p>Orem is the quiet powerhouse of Utah's technology and education corridor. Just north of Provo along University Parkway, Orem serves as the commercial and retail anchor of Utah County, with a population of over 97,000 that blends tech professionals, University Parkway students, young families, and established suburban households. The city is bisected by Interstate 15, making it equally accessible to both Silicon Slopes commuters heading north and BYU/UVU students heading south. This central position creates a unique passport demand profile: a mix of tech industry travel, university programs, and traditional suburban family vacations.</p>
                  <p>Utah Valley University, with its main campus extending into the northeast edge of Orem, enrolls over 40,000 students — the largest student body in the state. Its diverse international programs, from aviation to business to hospitality, generate steady passport demand. The University Parkway corridor is home to dozens of tech companies and business parks, sending employees on international business trips, conference circuits, and client site visits. The SCERA Theatre and surrounding cultural institutions also host international performing arts exchanges that require participant visas and passports.</p>
                  <p>Like all Utah Valley residents, Orem families face the geographic challenge of the Salt Lake City regional passport agency being 35 miles north with limited appointment availability. Local post offices along the University Parkway corridor and in neighboring Lindon and Pleasant Grove book passport acceptance slots weeks out during peak spring and summer seasons. Our courier service eliminates that bottleneck entirely. You send your documents from anywhere in Orem, and we handle the rest — delivering your completed passport back to you via FedEx overnight.</p>
                </div>
              </div>
              <div className="space-y-4">
                <img
                  src="https://readdy.ai/api/search-image?query=University%20Parkway%20area%20in%20Orem%20Utah%20with%20modern%20commercial%20buildings%20and%20technology%20campus%2C%20Mount%20Timpanogos%20visible%20in%20the%20dramatic%20background%2C%20suburban%20Utah%20Valley%20landscape%20with%20modern%20development%2C%20wide%20streets%20and%20commercial%20areas%2C%20warm%20golden%20hour%20light%2C%20clear%20mountain%20sky%2C%20professional%20cityscape%20photography%2C%20no%20readable%20text%20or%20signs&width=700&height=450&seq=orem-univparkway&orientation=landscape"
                  alt="University Parkway in Orem Utah with Mount Timpanogos"
                  className="w-full h-[250px] object-cover rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Nearest Passport Agency", value: "SLC — 35 miles north" },
                    { label: "UVU Enrollment", value: "40,000+ students" },
                    { label: "Airport", value: "Provo (PVU) / SLC International" },
                    { label: "Key Industries", value: "Tech, Education, Retail" },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#F7F8FA] rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                      <p className="font-bold text-[#1A365D] text-sm">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="travel-timing" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">When to Apply</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Orem Passport Timing Guide</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { scenario: "UVU Study Abroad", window: "Apply 3-4 weeks before departure", urgency: "Medium", icon: "ri-graduation-cap-line", detail: "UVU aviation, business, and hospitality programs have international components. Apply as soon as you're accepted." },
                { scenario: "LDS Mission Reassignment", window: "Apply within 24 hours of notice", urgency: "High", icon: "ri-global-line", detail: "International reassignments can happen with 48-72 hours notice. Our emergency service is your best option." },
                { scenario: "Tech Company Travel", window: "Apply as soon as trip is confirmed", urgency: "Variable", icon: "ri-briefcase-line", detail: "University Parkway tech companies book international travel on short notice. Renew immediately when your trip is confirmed." },
                { scenario: "Family International Vacation", window: "Apply 6-8 weeks in advance", urgency: "Low-Medium", icon: "ri-plane-line", detail: "Summer cruises and European trips. Standard expedited is sufficient but don't wait past 6 weeks." },
              ].map((item) => (
                <div key={item.scenario} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${item.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{item.scenario}</h3>
                  <p className="text-[#F6C344] text-xs font-bold mb-2 uppercase">{item.window}</p>
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-3 ${item.urgency === "High" ? "bg-red-100 text-red-700" : item.urgency === "Medium" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{item.urgency} Urgency</span>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="testimonials" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Real Stories</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Orem &amp; Utah Valley Clients</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: "Tyler B.", location: "Orem, UT", text: "UVU aviation program accepted for international flight training. Passport application was stuck at the post office. They got me processed in 6 days. Couldn't have started without them.", stars: 5 },
                { name: "Melissa K.", location: "Lindon, UT", text: "Family of 6 going to Cancun. Four kids needed passports. Everything was done in 8 days. They even walked me through the parental consent requirements.", stars: 5 },
                { name: "Chris H.", location: "Pleasant Grove, UT", text: "Tech company sent me to Singapore for a client meeting. Passport was expired. Renewed in 5 days. Never missed a beat at work.", stars: 5 },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#F6C344] text-sm"></i>)}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
                  <div>
                    <p className="font-bold text-[#1A365D] text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="facilities" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Offices</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Acceptance Facilities Near Orem</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-sm">First-time applicants and minors must visit an acceptance facility before we begin the courier process.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: "Orem Main Post Office", address: "760 S State St, Orem", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Provo Post Office", address: "95 W 100 S, Provo", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Lindon Post Office", address: "128 N State St, Lindon", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "Pleasant Grove Post Office", address: "210 S Main St, Pleasant Grove", hours: "Mon-Fri 9am-5pm", type: "Acceptance Facility" },
                { name: "SLC Passport Agency", address: "324 S State St, Suite 225, SLC", hours: "Mon-Fri 8am-3pm (by appt)", type: "Regional Agency (35 mi)" },
              ].map((fac) => (
                <div key={fac.name} className="border border-gray-200 rounded-lg p-6">
                  <span className="inline-block bg-[#EEF2F8] text-[#1A365D] text-xs font-semibold px-2 py-0.5 rounded-full mb-3">{fac.type}</span>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{fac.name}</h3>
                  <p className="text-gray-600 text-sm mb-1"><i className="ri-map-pin-line mr-1.5"></i>{fac.address}</p>
                  <p className="text-gray-500 text-xs"><i className="ri-time-line mr-1.5"></i>{fac.hours}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="compare" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Why Choose Us</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. Alternatives</h2>
            </div>

            <div className="bg-amber-50 border border-[#F6C344]/40 rounded-lg p-5 md:p-6 mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-bold text-base text-[#1A365D] mb-1">Passport Agency Appointment Not Available?</h3>
                  <p className="text-sm text-gray-600">Don't let a booked-out agency appointment block your travel plans. We bypass the agency bottleneck with reserved daily courier slots — faster and no appointment roulette.</p>
                </div>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Get Started <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A365D] text-white">
                    <th className="text-left px-4 py-3 font-semibold rounded-tl-lg">Feature</th>
                    <th className="text-center px-4 py-3 font-semibold text-[#F6C344]">Fast Passport Center</th>
                    <th className="text-center px-4 py-3 font-semibold">SLC Agency (35 mi)</th>
                    <th className="text-center px-4 py-3 font-semibold rounded-tr-lg">FedEx / UPS</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["No office visit required", "✓", "✗", "✗"],
                    ["As fast as 24 hours", "✓", "Same-day only", "✗"],
                    ["Price vs. resellers", "30-100% less", "Govt fee only", "Full markup"],
                    ["Registered State Dept. courier", "✓", "N/A", "✗"],
                    ["UVU student specialist", "✓", "Limited", "✗"],
                    ["Document error review", "✓", "✗", "✗"],
                    ["Handles family applications", "✓", "✗", "✗"],
                    ["FedEx delivery to your door", "✓", "Pick up in person", "Partial"],
                    ["A+ BBB rated", "✓", "N/A", "Varies"],
                  ].map(([feat, us, agency, fedex], i) => (
                    <tr key={feat} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}>
                      <td className="px-4 py-3 text-gray-700 font-medium">{feat}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-semibold">{us}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{agency}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{fedex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Orem Passport Mistakes</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Waiting for UVU program confirmation", desc: "Don't wait for final confirmation to start. Begin the passport process when you first apply to study abroad." },
                { title: "Underestimating post office wait times", desc: "Orem, Provo, and Lindon post offices book 2-4 weeks out in spring. Apply online immediately." },
                { title: "Not realizing the 6-month validity rule", desc: "Many countries require 6 months validity. Check your passport's expiration before booking." },
                { title: "Wrong photo background or dimensions", desc: "White background, 2x2 inches, no glasses. We review all submitted photos before courier dispatch." },
                { title: "Not applying for the whole family", desc: "Suburban families often need 4-5 passports. We process all family members together." },
                { title: "Sending documents to the wrong address", desc: "We provide a prepaid FedEx label with the exact address. No guessing, no confusion." },
              ].map((m) => (
                <div key={m.title} className="flex gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0 mt-0.5">
                    <i className="ri-close-circle-line text-red-600 text-base"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-sm mb-1">{m.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">The Process</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Orem Residents</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Order Online", desc: "Select your service and complete our secure order. 5 minutes, no paperwork to start." },
                { step: "2", title: "Mail Documents", desc: "Use our prepaid FedEx label from any drop location in Orem, Lindon, or Pleasant Grove." },
                { step: "3", title: "We Courier to D.C.", desc: "Our registered courier hand-delivers your application to the State Department in Washington, D.C." },
                { step: "4", title: "Delivered to Your Door", desc: "Your completed passport ships via FedEx Priority Overnight to your Orem area address." },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-xl mx-auto mb-4">{s.step}</div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="bg-white py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Questions</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Orem Passport FAQs</h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="border border-gray-200 rounded-lg group">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <h4 className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</h4>
                    <i className="ri-add-line text-[#1A365D] flex-shrink-0 group-open:hidden"></i>
                    <i className="ri-subtract-line text-[#1A365D] flex-shrink-0 hidden group-open:block"></i>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Official Links</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Orem Passport Applicants</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "U.S. State Dept. Passport Info", url: "https://travel.state.gov", icon: "ri-government-line" },
                { title: "SLC Passport Agency (35 mi north)", url: "https://travel.state.gov/content/travel/en/passports/get-fast/passport-agencies.html", icon: "ri-building-line" },
                { title: "Utah Valley University (UVU)", url: "https://www.uvu.edu", icon: "ri-graduation-cap-line" },
                { title: "DS-82 Renewal Form", url: "https://travel.state.gov/content/travel/en/passports/need-passport/ds-82.html", icon: "ri-file-text-line" },
                { title: "Provo Airport (PVU)", url: "https://www.provoairport.com", icon: "ri-flight-takeoff-line" },
                { title: "Utah DMV (ID Documentation)", url: "https://dld.utah.gov", icon: "ri-id-card-line" },
              ].map((r) => (
                <a key={r.title} href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-4 bg-white rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-colors group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                    <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <span className="text-[#1A365D] font-medium text-sm group-hover:text-[#F6C344] transition-colors">{r.title}</span>
                  <i className="ri-external-link-line text-gray-400 ml-auto"></i>
                </a>
              ))}
            </div>
          </div>
        </section>

        <SEOCrossLinks currentRoute="/cities/orem" state="UT" />

        <section className="bg-[#1A365D] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-4">Get Started Today</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">Orem's Fastest Passport Service</h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">UVU study abroad, LDS mission, family vacation, or business travel — we get it done. As fast as 24 hours, delivered to your Orem doorstep.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center justify-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
                Start My Passport <i className="ri-arrow-right-line"></i>
              </a>
              <a href="tel:8772530084" className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
                <i className="ri-phone-line"></i>877-253-0084
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}